/**
 * @fileoverview Polymorphic "Linked Records" block for every detail/anchor panel
 * @module components/jobs/phases/gather/summarization/shared-panels/LinkedRecordsSection
 *
 * Purpose:
 * - From a *detail* record (security interest, loan, ATO/SRO) lists every
 *   linked *anchor* (asset or creditor).
 * - From an *anchor* record (asset or creditor) lists every linked *detail*.
 * - Links are shown in priority (rank) order and can be reordered, unlinked,
 *   added (via `LinkPicker`), or clicked through to the counterpart record.
 *
 * The legacy SI-only behaviour is replaced by the polymorphic `recordLinks`
 * backend; every panel reaches the same component.
 */

import { useMemo } from "react";
import { ChevronDown, ChevronUp, Link2, Unlink } from "lucide-react";
import type { Id } from "@cvx/_generated/dataModel";
import { Badge } from "@/ui/primitives/badge";
import { cn } from "@/utils/misc";
import { LinkPicker } from "./LinkPicker";
import {
  counterpartOf,
  isAnchorType,
  linkableTypesFor,
  linksForRecord,
  orientLink,
  useJobLinks,
  useJobRecordIndex,
  useLinkMutations,
  RECORD_TYPE_ICON,
  RECORD_TYPE_BADGE_COLOR,
  type AnchorType,
  type DetailType,
  type LinkAnchor,
  type RecordRef,
  type RecordType,
} from "./linkShared";

interface LinkedRecordsSectionProps {
  jobId: Id<"jobs">;
  anchor: LinkAnchor;
  onOpenLinked: (type: RecordType, id: string) => void;
  isViewer?: boolean;
}

export function LinkedRecordsSection({
  jobId,
  anchor,
  onOpenLinked,
  isViewer,
}: LinkedRecordsSectionProps) {
  const links = useJobLinks(jobId);
  const records = useJobRecordIndex(jobId);
  const { link, unlink, reorder } = useLinkMutations();

  // Links belonging to this record's sibling set, already rank-ordered.
  const myLinks = useMemo(
    () => linksForRecord(links, anchor.type, anchor.id),
    [links, anchor.type, anchor.id],
  );

  const rows = myLinks.map((l) => {
    const counterpart = counterpartOf(l, anchor.id);
    const ref = records.byId.get(counterpart.id);
    return { link: l, ref };
  });

  // Candidate records for the picker — active records of every type that may
  // link to this viewer (see `linkableTypesFor`), minus those already linked.
  const candidates: RecordRef[] = useMemo(() => {
    const linkedIds = new Set(
      myLinks.map((l) => counterpartOf(l, anchor.id).id),
    );
    const poolByType: Record<RecordType, RecordRef[]> = {
      security_interest: records.securityInterests,
      asset: records.assets,
      creditor: records.creditors,
      loan: records.loans,
      ato_sro: records.atoSro,
    };
    const pool = linkableTypesFor(anchor.type).flatMap((t) => poolByType[t]);
    return pool.filter((r) => r.id !== anchor.id && !linkedIds.has(r.id));
  }, [myLinks, records, anchor.type, anchor.id]);

  const handlePick = (ref: RecordRef) => {
    // Orient the pair canonically so the SI is always the detail and the loan
    // the anchor for SI↔Loan, regardless of which panel we're on.
    const oriented = orientLink(
      { type: anchor.type, id: anchor.id },
      { type: ref.type, id: ref.id },
    );
    link.mutate({
      detailType: oriented.detailType,
      detailId: oriented.detailId as Id<"securityInterestEntries">,
      anchorType: oriented.anchorType,
      anchorId: oriented.anchorId as Id<"assetEntries">,
    });
  };

  const reorderTo = (orderedIds: string[]) => {
    // Log the reorder on the viewer's natural side: assets/creditors are
    // anchors; SI/loan/ATO log on the detail side (a loan's reorder history
    // lands on the loan row via the detail path).
    if (isAnchorType(anchor.type)) {
      reorder.mutate({
        orderedLinkIds: orderedIds as Id<"recordLinks">[],
        anchorKind: "anchor",
        anchorTypeIfAnchor: anchor.type as AnchorType,
        anchorIdIfAnchor: anchor.id as Id<"assetEntries">,
      });
    } else {
      reorder.mutate({
        orderedLinkIds: orderedIds as Id<"recordLinks">[],
        anchorKind: "detail",
        anchorTypeIfDetail: anchor.type as DetailType,
        anchorIdIfDetail: anchor.id as Id<"securityInterestEntries">,
      });
    }
  };

  // Swap two links in the global rank order — used to reorder within a type
  // group (an item only ever moves past a same-type sibling).
  const swapLinks = (aId: string, bId: string) => {
    const ordered = myLinks.map((l) => l._id);
    const ia = ordered.indexOf(aId);
    const ib = ordered.indexOf(bId);
    if (ia < 0 || ib < 0) return;
    [ordered[ia], ordered[ib]] = [ordered[ib], ordered[ia]];
    reorderTo(ordered);
  };

  // Group the linked records by counterpart type, preserving global rank order
  // within each group, so the calc reads cleanly (collateral assets together,
  // debt creditors/SIs together, etc.).
  const groups = useMemo(() => {
    const order: RecordType[] = linkableTypesFor(anchor.type);
    const out: { type: RecordType | null; rows: typeof rows }[] = order
      .map((type) => ({
        type: type as RecordType | null,
        rows: rows.filter((r) => r.ref?.type === type),
      }))
      .filter((g) => g.rows.length > 0);
    const known = new Set(order);
    const others = rows.filter((r) => !r.ref || !known.has(r.ref.type));
    if (others.length > 0) out.push({ type: null, rows: others });
    return out;
  }, [rows, anchor.type]);

  return (
    <div className="space-y-2.5">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h4 className="font-sans text-xs font-semibold uppercase tracking-wider text-[#121212]">
            Linked Records
          </h4>
          {rows.length > 0 && (
            <Badge variant="secondary" size="sm">
              {rows.length}
            </Badge>
          )}
        </div>
        {!isViewer && (
          <LinkPicker candidates={candidates} onPick={handlePick} />
        )}
      </div>

      {rows.length === 0 ? (
        <p className="text-xs text-muted-foreground">No linked records yet.</p>
      ) : (
        <div className="space-y-3">
          {groups.map((group) => {
            const GroupIcon = group.type ? RECORD_TYPE_ICON[group.type] : Link2;
            return (
              <div key={group.type ?? "other"} className="space-y-1.5">
                {/* Type subheader */}
                <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                  <GroupIcon className="h-3 w-3" />
                  {group.type ? GROUP_LABEL[group.type] : "Other"}
                </p>
                {group.rows.map(({ link: l, ref }, gi) => {
                  const RowIcon = ref ? RECORD_TYPE_ICON[ref.type] : Link2;
                  return (
                    <div
                      key={l._id}
                      className="flex items-center gap-2 rounded-lg border px-2 py-1.5"
                    >
                      {/* Reorder controls — within the type group only */}
                      {!isViewer && group.rows.length > 1 && (
                        <div className="flex shrink-0 flex-col">
                          <button
                            disabled={gi === 0}
                            onClick={() =>
                              swapLinks(l._id, group.rows[gi - 1].link._id)
                            }
                            className="text-muted-foreground hover:text-foreground disabled:opacity-25"
                          >
                            <ChevronUp className="h-3 w-3" />
                          </button>
                          <button
                            disabled={gi === group.rows.length - 1}
                            onClick={() =>
                              swapLinks(l._id, group.rows[gi + 1].link._id)
                            }
                            className="text-muted-foreground hover:text-foreground disabled:opacity-25"
                          >
                            <ChevronDown className="h-3 w-3" />
                          </button>
                        </div>
                      )}

                      {/* Counterpart */}
                      <button
                        onClick={() => ref && onOpenLinked(ref.type, ref.id)}
                        disabled={!ref}
                        className={cn(
                          "min-w-0 flex-1 text-left",
                          ref && "transition-opacity hover:opacity-70",
                        )}
                      >
                        <div className="flex items-center gap-1.5">
                          <RowIcon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                          <span className="truncate font-sans text-sm font-medium">
                            {ref?.title ?? "Unavailable record"}
                          </span>
                          {ref?.amount != null && (
                            <Badge
                              color={RECORD_TYPE_BADGE_COLOR[ref.type]}
                              variant="soft"
                              size="sm"
                              className="shrink-0 tabular-nums"
                            >
                              {fmtLinkAmount(ref.amount)}
                            </Badge>
                          )}
                        </div>
                        {ref?.subtitle && (
                          <div className="truncate pl-[18px] font-sans text-xs text-muted-foreground">
                            {ref.subtitle}
                          </div>
                        )}
                      </button>

                      {/* Unlink */}
                      {!isViewer && (
                        <button
                          onClick={() =>
                            unlink.mutate({
                              linkId: l._id as Id<"recordLinks">,
                            })
                          }
                          title="Unlink"
                          className="shrink-0 text-muted-foreground transition-colors hover:text-destructive"
                        >
                          <Unlink className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Plural section labels for the linked-records type groups. */
const GROUP_LABEL: Record<RecordType, string> = {
  asset: "Assets",
  creditor: "Creditors",
  security_interest: "Security interests",
  loan: "Loans",
  ato_sro: "ATO / SRO",
};

/** Compact AUD for the value badge (no cents). */
function fmtLinkAmount(n: number): string {
  return `$${n.toLocaleString("en-AU", { maximumFractionDigits: 0 })}`;
}
